import { navigationService } from './navigation.service';
import { vendorService } from './vendor.service';
import { legalService } from './legal.service';
import type { HomeIndicatorSource } from '@/config/home-modules';

export interface HomeIndicator {
  label: string;
  value: number;
}

/**
 * Best-effort headline indicators for a home-dashboard module card (P1).
 *
 * These are intentionally non-blocking: every loader catches its own failures
 * and returns `[]`, so the card always renders even when a backing service is
 * down. The page treats indicators as decorative — module cards and their
 * navigation never depend on this data resolving.
 */
class HomeService {
  async getSecurityIndicators(): Promise<HomeIndicator[]> {
    try {
      const stats = await navigationService.getNavigationStats();
      return [
        { label: 'Open incidents', value: stats.incidents },
        { label: 'Active threats', value: stats.threats },
        { label: 'Open investigations', value: stats.investigations },
        { label: 'High-risk EP', value: stats.executiveProtection },
      ];
    } catch {
      return [];
    }
  }

  async getLegalIndicators(): Promise<HomeIndicator[]> {
    // Pull just the pagination totals (limit: 1) from each list endpoint.
    // Settle independently so one failing service does not drop the others.
    const [vendors, contracts, matters] = await Promise.allSettled([
      vendorService.getVendors({ limit: 1 }),
      vendorService.getContracts({ limit: 1 }),
      legalService.getMatters({ limit: 1 }),
    ]);

    const indicators: HomeIndicator[] = [];
    if (vendors.status === 'fulfilled') {
      indicators.push({ label: 'Vendors', value: vendors.value.pagination.total });
    }
    if (contracts.status === 'fulfilled') {
      indicators.push({ label: 'Contracts', value: contracts.value.pagination.total });
    }
    if (matters.status === 'fulfilled') {
      indicators.push({ label: 'Legal matters', value: matters.value.pagination.total });
    }
    return indicators;
  }

  getIndicators(source: HomeIndicatorSource): Promise<HomeIndicator[]> {
    return source === 'security'
      ? this.getSecurityIndicators()
      : this.getLegalIndicators();
  }
}

export const homeService = new HomeService();
